import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import SalonStartCanvasButton from '../components/SalonStartCanvasButton';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { useSkinSession } from '../context/SkinSessionContext';
import { captureDeviceInfo } from '../utils/deviceCapture';
import { salonAppPath, salonLastCustomerStorageKey } from '../config/salonApp';
import { clearSession } from '../utils/sessionManager';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { applyPageSetupResult, storePageSetupData, getPageSetupDataKey } from '../components/PageBackgroundSync';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import '../styles/PageBackground.css';
import './LandingPage.css';

/** Extra wait after canvas animation before the redirect transition. */
const SALON_CTA_POST_ANIMATION_DELAY_MS = 0;

/** Exit curtain duration (must match `.salon-redirect-overlay` in LandingPage.css). */
const SALON_REDIRECT_EXIT_MS = 450;

function getSalonRedirectExitDurationMs() {
  if (typeof window === 'undefined' || !window.matchMedia) return SALON_REDIRECT_EXIT_MS;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : SALON_REDIRECT_EXIT_MS;
}

/** Decode HTML entities so escaped API content (e.g. &lt;h1&gt;) renders as real HTML */
function decodeHtmlEntities(html) {
  if (!html || typeof html !== 'string') return html;
  const entities = { '&lt;': '<', '&gt;': '>', '&amp;': '&', '&quot;': '"', '&#39;': "'", '&apos;': "'" };
  return html.replace(/&(?:lt|gt|amp|quot|#39|apos);/g, (m) => entities[m] || m);
}

const LandingPage = () => {
  const navigate = useNavigate();
  const { token } = useSkinSession();
  const [imageUrl, setImageUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ctaPosition, setCtaPosition] = useState('top');
  const [ctaText, setCtaText] = useState('Start Skin Analysis');
  const [ctaTheme, setCtaTheme] = useState({
    primaryColor: '',
    secondaryColor: '',
    textColor: '',
  });
  const [orgLogoUrl, setOrgLogoUrl] = useState('');
  const [backgroundReady, setBackgroundReady] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  /** After tap animation completes: delay, then session — keeps canvas in loading animation */
  const [ctaFlowActive, setCtaFlowActive] = useState(false);
  const [desktopLandscape, setDesktopLandscape] = useState(false);
  const [redirectAnimating, setRedirectAnimating] = useState(false);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  const descriptionScaleClass = useMemo(() => {
    const decoded = decodeHtmlEntities(description || '');
    const plain = stripHtml(decoded || '').replace(/\s+/g, ' ').trim();
    const len = plain.length;
    if (len >= 1200) return 'desc-xxl';
    if (len >= 850) return 'desc-xl';
    if (len >= 550) return 'desc-lg';
    return '';
  }, [description]);

  const dynamicScaleVars = useMemo(() => {
    const decoded = decodeHtmlEntities(description || '');
    const plain = stripHtml(decoded || '').replace(/\s+/g, ' ').trim();
    const len = plain.length;
    const vw = viewportSize.width || 1280;
    const vh = viewportSize.height || 800;
    const isMobile = vw <= 768;
    const isTablet = vw > 768 && vw <= 1024;
    const density = len / Math.max(vh, 1);

    let descScale = 1;
    let headingScale = 1;
    let imageScale = 1;

    if (len >= 550) {
      descScale -= 0.07;
      imageScale -= 0.06;
    }
    if (len >= 850) {
      descScale -= 0.08;
      imageScale -= 0.08;
    }
    if (len >= 1200) {
      descScale -= 0.09;
      imageScale -= 0.1;
    }

    if (density > 1.1) {
      descScale -= 0.04;
      imageScale -= 0.03;
    }
    if (density > 1.5) {
      descScale -= 0.05;
      imageScale -= 0.04;
    }

    if (isMobile) {
      if (vh < 760) {
        descScale -= 0.06;
        imageScale -= 0.08;
      }
      if (vh < 680) {
        descScale -= 0.07;
        imageScale -= 0.1;
      }
    } else if (isTablet) {
      if (vh < 860) {
        descScale -= 0.04;
        imageScale -= 0.05;
      }
    }

    descScale = Math.max(0.72, Math.min(1, descScale));
    imageScale = Math.max(0.58, Math.min(1, imageScale));
    headingScale = Math.max(0.78, Math.min(1, descScale + 0.08));

    return {
      '--landing-desc-scale': String(descScale),
      '--landing-heading-scale': String(headingScale),
      '--landing-image-scale': String(imageScale),
    };
  }, [description, viewportSize.height, viewportSize.width]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1025px) and (orientation: landscape)');
    const sync = () => setDesktopLandscape(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/salon/login', { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) setBackgroundReady(true);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const key = getPageSetupDataKey(token);
    if (!key) return;
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        const ext = parsed?.external_fields;
        if (ext?.org_logo && typeof ext.org_logo === 'string') setOrgLogoUrl(ext.org_logo);
      }
    } catch (_) {}
  }, [token]);

  const fetchedTokenRef = useRef('');
  useEffect(() => {
    if (!token) return;
    if (fetchedTokenRef.current === token) return;
    fetchedTokenRef.current = token;

    const landingUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/landing-page`;
    const pageSetupUrl = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup`;

    const fetchLandingData = async () => {
      setIsLoading(true);
      try {
        const [landingRes, pageSetupRes] = await Promise.all([
          fetchWithTimeout(landingUrl, { timeoutMs: 20000 }),
          fetchWithTimeout(pageSetupUrl, { timeoutMs: 20000 }),
        ]);

        if (pageSetupRes.ok) {
          const pageSetupResult = await pageSetupRes.json();
          applyPageSetupResult(pageSetupResult);
          storePageSetupData(token, pageSetupResult);
          const ext = pageSetupResult?.external_fields;
          if (ext?.org_logo && typeof ext.org_logo === 'string') setOrgLogoUrl(ext.org_logo);
        }

        if (landingRes.ok) {
          const result = await landingRes.json();
          const data = result.data || result;
          const remoteThumbnail = data?.thumbnail || (Array.isArray(result) && result[0]?.thumbnail);
          if (remoteThumbnail) setImageUrl(remoteThumbnail);

          const remoteDescription = data?.description || (Array.isArray(result) && result[0]?.description);
          if (remoteDescription) setDescription(remoteDescription);

          const position = data?.cta_position || (Array.isArray(result) && result[0]?.cta_position);
          if (position === 'top' || position === 'bottom') setCtaPosition(position);

          const remoteCtaText = data?.cta_text || (Array.isArray(result) && result[0]?.cta_text);
          if (typeof remoteCtaText === 'string' && remoteCtaText.trim()) {
            setCtaText(remoteCtaText.trim());
          }

          const remoteCtaColor = data?.cta_color || (Array.isArray(result) && result[0]?.cta_color);
          const remoteButton = data?.button || (Array.isArray(result) && result[0]?.button);
          const primaryColor = typeof remoteButton?.primary_color === 'string' ? remoteButton.primary_color.trim() : '';
          const secondaryColor = typeof remoteButton?.secondary_color === 'string' ? remoteButton.secondary_color.trim() : '';
          const textColorFromButton = typeof remoteButton?.text_color === 'string' ? remoteButton.text_color.trim() : '';
          const textColorFromCta = typeof remoteCtaColor === 'string' ? remoteCtaColor.trim() : '';

          setCtaTheme({
            primaryColor,
            secondaryColor,
            textColor: textColorFromButton || textColorFromCta,
          });
        }
      } catch (_) {
        // show fallback on error
      } finally {
        setIsLoading(false);
        setBackgroundReady(true);
      }
    };

    fetchLandingData();
  }, [token]);

  const handleStartAfterCanvas = async () => {
    const deviceInfo = captureDeviceInfo();
    setIsButtonLoading(true);
    try {
      clearSession();
      if (token) {
        localStorage.removeItem(salonLastCustomerStorageKey(token));
      }
    } catch (_) {}

    const exitMs = getSalonRedirectExitDurationMs();
    const navState = {
      ...(token ? { token } : {}),
      deviceInfo,
      deferSession: true,
    };

    if (exitMs === 0) {
      try {
        navigate(salonAppPath('/user-info'), { state: navState });
      } finally {
        setIsButtonLoading(false);
        setCtaFlowActive(false);
      }
      return;
    }

    setRedirectAnimating(true);
    window.setTimeout(() => {
      try {
        navigate(salonAppPath('/user-info'), { state: navState });
      } finally {
        setIsButtonLoading(false);
        setCtaFlowActive(false);
      }
    }, exitMs);
  };

  const handleCanvasAnimationComplete = () => {
    setCtaFlowActive(true);
    window.setTimeout(() => {
      void handleStartAfterCanvas();
    }, SALON_CTA_POST_ANIMATION_DELAY_MS);
  };

  return (
    <div
      className={`landing-page-shell page-bg${!backgroundReady ? ' landing-bg-pending' : ''}${redirectAnimating ? ' salon-redirect-exit' : ''}`}
      id="landing-page-shell"
    >
      <div className="landing-page-nav" id="landing-page-nav">
        <SalonHeaderActions homeButtonClassName="landing-page-home-button" />
      </div>
      {redirectAnimating && (
        <div className="salon-redirect-overlay" aria-hidden="true" />
      )}
      {isLoading && (
        <div className="landing-page-loading-overlay" id="landing-page-loading-overlay" aria-busy="true" aria-live="polite">
          <div className="landing-page-loading-content" style={{background: 'none', boxShadow: 'none', border: 'none', padding: 0}}>
            <div className="landing-page-loading-spinner" aria-hidden="true" />
            <p className="landing-page-loading-text">Launching Skin Analyzer…</p>
          </div>
        </div>
      )}
      <main className="landing-page-main" id="landing-page-main">
        <div className="landing-page-container" id="landing-page-container">
          <div
            className={`landing-page-desktop-layout ${ctaPosition === 'bottom' ? 'cta-bottom' : 'cta-top'} ${descriptionScaleClass}`.trim()}
            id="landing-page-desktop-layout"
            style={dynamicScaleVars}
          >
            <div className="landing-page-desktop-content" id="landing-page-desktop-content">
              <div className="landing-page-desktop-text" id="landing-page-desktop-text">
                <div className="landing-page-desktop-logo-wrapper" id="landing-page-desktop-logo-wrapper">
                  <img
                    src={orgLogoUrl || `${process.env.PUBLIC_URL}/logo.svg`}
                    alt="Organization logo"
                    className="landing-page-desktop-logo"
                    id="landing-page-desktop-logo"
                  />
                </div>
                <div
                  className={`landing-page-desktop-description ${descriptionScaleClass}`.trim()}
                  id="landing-page-desktop-description"
                  dangerouslySetInnerHTML={description ? { __html: sanitizeHtml(decodeHtmlEntities(description)) } : undefined}
                />
                {ctaPosition !== 'bottom' || desktopLandscape ? (
                  <SalonStartCanvasButton
                    key="salon-start-cta-primary"
                    className="cta-in-content"
                    label={ctaText}
                    primaryColor={ctaTheme.primaryColor}
                    secondaryColor={ctaTheme.secondaryColor}
                    textColor={ctaTheme.textColor}
                    onComplete={handleCanvasAnimationComplete}
                    disabled={ctaFlowActive || isButtonLoading}
                  />
                ) : null}
              </div>
            </div>

            {imageUrl && (
              <div className="landing-page-desktop-image" id="landing-page-desktop-image">
                <img
                  src={imageUrl}
                  alt="AI Skin Analysis Visualization"
                  className="landing-page-desktop-img"
                  id="landing-page-desktop-img"
                  style={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.3s ease' }}
                />
              </div>
            )}

            {ctaPosition === 'bottom' && !desktopLandscape ? (
              <SalonStartCanvasButton
                key="salon-start-cta-below"
                className="cta-below-image"
                label={ctaText}
                primaryColor={ctaTheme.primaryColor}
                secondaryColor={ctaTheme.secondaryColor}
                textColor={ctaTheme.textColor}
                onComplete={handleCanvasAnimationComplete}
                disabled={ctaFlowActive || isButtonLoading}
              />
            ) : null}
          </div>
        </div>
      </main>

      <PageFooter />
    </div>
  );
};

export default LandingPage;
